import React, { useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  BodyHorizontalScroll,
  CarouselContainer,
  BodyHorizontalScrollBody,
} from "./styled";

const DRAG_THRESHOLD = 6;

const HorizontalScrollCarousel = ({ children, className, gap, grid, small, viewAll, nospace }) => {
  const scrollerRef = useRef(null);
  const pointerStateRef = useRef({
    isPointerDown: false,
    isDragging: false,
    pointerId: null,
    startPosition: 0,
    scrollLeft: 0,
  });
  const suppressClickRef = useRef(false);
  const [isDragging, setIsDragging] = useState(false);

  const setCursor = (value) => {
    if (!scrollerRef.current) return;
    scrollerRef.current.style.cursor = value;
  };

  const finishDrag = () => {
    if (!pointerStateRef.current.isPointerDown && !pointerStateRef.current.isDragging) return;

    pointerStateRef.current.isPointerDown = false;
    pointerStateRef.current.isDragging = false;
    pointerStateRef.current.pointerId = null;
    setIsDragging(false);
    setCursor("grab");
  };

  const handlePointerDown = (event) => {
    if (!scrollerRef.current || event.button > 0) return;

    pointerStateRef.current.isPointerDown = true;
    pointerStateRef.current.isDragging = false;
    pointerStateRef.current.pointerId = event.pointerId;
    pointerStateRef.current.startPosition = event.clientX;
    pointerStateRef.current.scrollLeft = scrollerRef.current.scrollLeft;
    suppressClickRef.current = false;

    setCursor("grab");
  };

  const handlePointerMove = (event) => {
    if (!scrollerRef.current || !pointerStateRef.current.isPointerDown) return;

    const walk = event.clientX - pointerStateRef.current.startPosition;

    if (!pointerStateRef.current.isDragging && Math.abs(walk) >= DRAG_THRESHOLD) {
      pointerStateRef.current.isDragging = true;
      suppressClickRef.current = true;
      setIsDragging(true);
      setCursor("grabbing");
    }

    if (!pointerStateRef.current.isDragging) return;

    event.preventDefault();

    const maxScrollLeft = scrollerRef.current.scrollWidth - scrollerRef.current.clientWidth;
    const newScrollLeft = pointerStateRef.current.scrollLeft - walk;
    scrollerRef.current.scrollLeft = Math.max(0, Math.min(newScrollLeft, maxScrollLeft));
  };

  const handleClickCapture = (event) => {
    if (!suppressClickRef.current) return;

    event.preventDefault();
    event.stopPropagation();
    suppressClickRef.current = false;
  };

  return (
    <CarouselContainer className={className}>
      <BodyHorizontalScroll
        ref={scrollerRef}
        $grid={grid}
        gap={gap}
        small={small}
        viewAll={viewAll}
        onClickCapture={handleClickCapture}
        onPointerDown={handlePointerDown}
        onPointerLeave={finishDrag}
        onPointerUp={finishDrag}
        onPointerCancel={finishDrag}
        onPointerMove={handlePointerMove}
        style={{ cursor: isDragging ? "grabbing" : "grab" }}
      >
        <BodyHorizontalScrollBody $grid={grid} gap={gap} viewAll={viewAll} nospace={nospace}>
          {children}
        </BodyHorizontalScrollBody>
      </BodyHorizontalScroll>
    </CarouselContainer>
  );
};

HorizontalScrollCarousel.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  gap: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  grid: PropTypes.bool,
  small: PropTypes.bool,
  viewAll: PropTypes.bool,
};

HorizontalScrollCarousel.defaultProps = {
  className: undefined,
  gap: 24,
  grid: false,
  small: false,
  viewAll: false,
  nospace: false,
};

export default HorizontalScrollCarousel;
